import { ChevronDown } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useRefetchableFragment } from "react-relay";
import { JobRunStatus } from "src/components/JobRunDetails/JobRunStatus";
import { Shimmer } from "src/components/ui/shimmer";

import { ChatMessageClipboard } from "../ui/chat/ChatMessageClipboard";
import { MessageWithToolReferences } from "../ui/chat/message-with-tool-references";
import { ToolCallSheet } from "../ui/chat/tool-calls/ToolCallSheet";
import { V2AgentThoughtProcessRenderer } from "../ui/chat/tool-calls/V2AgentThoughtProcessRenderer";
import { isFilesystemTool } from "../ui/chat/tool-calls/v2Utils";
import {
  AlertResponderConversationWithRefetch_llmConversation$data,
  AlertResponderConversationWithRefetch_llmConversation$key,
} from "./__generated__/AlertResponderConversationWithRefetch_llmConversation.graphql";

type AgentMessage = NonNullable<
  AlertResponderConversationWithRefetch_llmConversation$data["messages"]
>[number];

interface InvestigationThoughtProcessProps {
  agentThoughtProcess: AgentMessage["agentThoughtProcess"];
  startedAt: string;
  endedAt: string | null | undefined;
  isThinking: boolean;
}

const InvestigationThoughtProcess = React.memo<InvestigationThoughtProcessProps>(
  function InvestigationThoughtProcess({ agentThoughtProcess, startedAt, endedAt, isThinking }) {
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [activeToolIndex, setActiveToolIndex] = useState(0);

    // Get all clickable tool uses (exclude filesystem tools and UpdateTodoTool)
    const toolUses = useMemo(() => {
      return (agentThoughtProcess || [])
        .map((item, index) => ({ toolUse: item.toolUse, originalIndex: index }))
        .filter((item) => {
          if (!item.toolUse) return false;
          const toolName = item.toolUse.toolUse?.name;
          if (!toolName) return false;
          if (toolName === "UpdateTodoTool" || toolName === "TodoWrite") return false;
          return !isFilesystemTool(toolName);
        });
    }, [agentThoughtProcess]);

    const handleToolClick = useCallback(
      (index: number) => {
        const toolIndex = toolUses.findIndex((item) => item.originalIndex === index);
        if (toolIndex !== -1) {
          setActiveToolIndex(toolIndex);
          setIsSheetOpen(true);
        }
      },
      [toolUses]
    );

    const moveToNextTool = useCallback(() => {
      if (activeToolIndex < toolUses.length - 1) {
        setActiveToolIndex(activeToolIndex + 1);
      }
    }, [activeToolIndex, toolUses.length]);

    const moveToPrevTool = useCallback(() => {
      if (activeToolIndex > 0) {
        setActiveToolIndex(activeToolIndex - 1);
      }
    }, [activeToolIndex]);

    const activeTool = toolUses[activeToolIndex]?.toolUse;

    if (!agentThoughtProcess || agentThoughtProcess.length === 0) {
      return null;
    }

    return (
      <>
        <V2AgentThoughtProcessRenderer
          items={agentThoughtProcess}
          isThinking={isThinking}
          startedAt={startedAt}
          endedAt={endedAt}
          onToolClick={handleToolClick}
        />
        {isSheetOpen && activeTool && (
          <ToolCallSheet
            toolCall={activeTool}
            index={activeToolIndex}
            totalCount={toolUses.length}
            isOpen={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            onNext={moveToNextTool}
            onPrevious={moveToPrevTool}
          />
        )}
      </>
    );
  }
);

interface AlertResponderConversationWithRefetchProps {
  llmConversation: AlertResponderConversationWithRefetch_llmConversation$key;
  outputJson: {
    result?: string;
  };
  status: string;
}

const llmConversationFragment = graphql`
  fragment AlertResponderConversationWithRefetch_llmConversation on GraphQLLlmConversation
  @refetchable(queryName: "AlertResponderConversationWithRefetchRefetchQuery") {
    id
    threadState
    messages {
      id
      messageRole
      messageType
      content
      startedAt
      endedAt
      agentThoughtProcess {
        text
        thinking
        thinkingSummary
        subagentDescription
        subagentOutput
        subagentItems {
          text
          thinking
          thinkingSummary
          toolUse {
            toolUse {
              id
              name
            }
            ...V2AgentThoughtProcessRenderer_toolCall
            ...ChecklistToolCallResult_toolCall
            ...ToolCallSheet_toolCall
            ...SkillCluster_toolCall
          }
        }
        toolUse {
          toolUse {
            id
            name
          }
          ...V2AgentThoughtProcessRenderer_toolCall
          ...ChecklistToolCallResult_toolCall
          ...ToolCallSheet_toolCall
          ...SkillCluster_toolCall
        }
      }
      ...messageWithToolReferences_formattedMessage
    }
  }
`;

export const AlertResponderConversationWithRefetch: React.FC<
  AlertResponderConversationWithRefetchProps
> = ({ llmConversation: llmConversationKey, outputJson, status }) => {
  const [llmConversation] = useRefetchableFragment(
    llmConversationFragment,
    llmConversationKey
  );

  // TODO: This is not working as expected. We are relying on the parent conversation to trigger the refetch for now.
  // usePusherEvent<{ id: string }>({
  //   channelId: conversation.id,
  //   eventType: "JOB_RUN_CONVERSATION_UPDATED",
  //   onEvent: (eventData) => {
  //     if (eventData.id !== conversation.id) return;

  //     startTransition(() => {
  //       refetch({}, { fetchPolicy: "network-only" });
  //     });
  //   },
  // });

  const isThinking = llmConversation?.threadState === "PENDING";

  // Get the last agent message from the messages array
  const subAgentMessage = React.useMemo(() => {
    if (!llmConversation?.messages) return undefined;
    const agentMessages = llmConversation.messages.filter(m => m.messageRole === "AGENT");
    return agentMessages.length > 0 ? agentMessages[agentMessages.length - 1] : undefined;
  }, [llmConversation?.messages]);

  const resultText = outputJson?.result;

  // Determine if investigation is completed (has final result)
  const investigationCompleted = !!resultText;

  // State for accordion sections
  const [openSections, setOpenSections] = useState<string[]>([]);

  // Reset accordion state when conversation changes
  useEffect(() => {
    setOpenSections(["summary"]);
  }, [llmConversation.id]);

  // Scroll to top when investigation completes
  useEffect(() => {
    if (investigationCompleted) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [investigationCompleted]);

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  return (
    <div className="space-y-4">
      {/* Investigation Summary Accordion - always show */}
      <div className="bg-muted border border-border rounded-lg overflow-hidden transition-all duration-200">
        <div
          className="px-5 py-3 cursor-pointer flex items-center justify-between hover:bg-muted/80 transition-all duration-200"
          onClick={() => toggleSection("summary")}
        >
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-foreground">Investigation Summary</span>
            <JobRunStatus status={status} variant="badge" size="xs" />
          </div>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
              openSections.includes("summary") ? "rotate-180" : ""
            }`}
          />
        </div>
        <div
          className={`overflow-hidden transition-all duration-200 ${
            openSections.includes("summary")
              ? "opacity-100"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-5 py-4 border-t border-border">
            {subAgentMessage ? (
              <div className="space-y-3">
                {/* Agent Thought Process */}
                <InvestigationThoughtProcess
                  agentThoughtProcess={subAgentMessage.agentThoughtProcess}
                  startedAt={subAgentMessage.startedAt}
                  endedAt={subAgentMessage.endedAt}
                  isThinking={isThinking}
                />
                {/* Investigation Summary Content - only show when investigation is completed */}
                {investigationCompleted && resultText && (
                  <MessageWithToolReferences
                    message={subAgentMessage}
                    messageContentOverride={resultText}
                    copyButton={
                      <ChatMessageClipboard
                        messageContent={resultText}
                      />
                    }
                  />
                )}
              </div>
            ) : (
              <Shimmer lines={3} seed={llmConversation.id} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
